import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { getUserHolding } from "../../redux/holdings/holdingSlice";
import {
  extractHoldingsList,
  extractHoldingsPagination,
  getHoldingsErrorMessage,
  HoldingsApiPayload,
  isHoldingsApiError,
} from "../../utils/holdingsApiUtils";

export const useUserHoldingsFetch = <T,>(holdingType: string, errorFallback: string) => {
  const dispatch = useDispatch<any>();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [data, setData] = useState<T[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const fetchInFlightRef = useRef(false);

  const fetchHoldings = useCallback(async () => {
    if (fetchInFlightRef.current) return;

    fetchInFlightRef.current = true;
    setLoader(true);

    try {
      const response = await dispatch(
        getUserHolding({
          userId: Number(id),
          holdingType,
        })
      );

      if (getUserHolding.rejected.match(response)) {
        setErrorMessage(getHoldingsErrorMessage(response.payload, errorFallback));
        setData([]);
        return;
      }

      const payload = response.payload as HoldingsApiPayload;
      if (isHoldingsApiError(payload)) {
        setErrorMessage(getHoldingsErrorMessage(payload, errorFallback));
        setData([]);
        return;
      }

      setData(extractHoldingsList(payload) as T[]);
      const pagination = extractHoldingsPagination(payload);
      setTotalPages(pagination.totalPages);
      setCurrentPage(pagination.pageIndex);
    } catch (error) {
      console.error(`Error fetching ${holdingType} holdings:`, error);
      setErrorMessage(errorFallback);
      setData([]);
    } finally {
      fetchInFlightRef.current = false;
      setLoader(false);
    }
  }, [dispatch, errorFallback, holdingType, id]);

  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return {
    loader,
    errorMessage,
    setErrorMessage,
    data,
    pageSize,
    totalPages,
    currentPage,
    handlePageChange,
    handlePageSizeChange,
    fetchHoldings,
  };
};
